/**
 * AUD value of one Société point when redeemed on CLASSIC (non-flash) rewards — the revenue LE
 * recognises per point in the classic-rewards-margin formula. This is a loyalty-wide constant,
 * NOT part of the flash catalog config (none of its 9 keys carry it); if it ever becomes
 * configurable the service must add a config key first.
 */
export const CLASSIC_REWARD_POINT_VALUE_AUD = 0.005;

/**
 * Classic rewards margin for a Société reward price:
 * 1 − COGS ÷ (points × CLASSIC_REWARD_POINT_VALUE_AUD + copay).
 * AUD-denominated, base stay only. Null when COGS is absent/non-positive or the denominator is 0.
 */
export function classicRewardsMargin({
  cogsAud,
  points,
  copayAud,
}: {
  cogsAud: number | null | undefined;
  points: number | null | undefined;
  copayAud: number | null | undefined;
}): number | null {
  if (cogsAud == null || cogsAud <= 0) return null;
  const denominator = (points ?? 0) * CLASSIC_REWARD_POINT_VALUE_AUD + (copayAud ?? 0);
  if (denominator <= 0) return null;
  return 1 - cogsAud / denominator;
}

/**
 * Value per point (VPP) for a Société reward, in AUD CENTS per point, mirroring the pricing
 * service's own `valuePerPointCents`: LuxPlus+ AUD price × 100 ÷ points, rounded to 4 dp — the
 * co-pay is deliberately NOT subtracted, so the number shown here is the same one the service
 * gates against `vppFloorCents`/`vppCeilingCents`. Null when the LuxPlus+ price or points is
 * absent/non-positive.
 */
export function valuePerPointCents({
  luxPlusPriceAud,
  points,
}: {
  luxPlusPriceAud: number | null | undefined;
  points: number | null | undefined;
}): number | null {
  if (luxPlusPriceAud == null || luxPlusPriceAud <= 0) return null;
  if (points == null || points <= 0) return null;
  return Math.round((luxPlusPriceAud * 100 * 10000) / points) / 10000;
}

/** Where a VPP sits relative to the configured eligibility window (inclusive at both bounds). */
export type VppWindowState = 'below_floor' | 'within' | 'above_ceiling';

/**
 * Classifies a VPP (cents per point) against the flash config's VPP window, mirroring the
 * service's gates (`< vppFloorCents` → below, `> vppCeilingCents` → above). Null when the VPP or
 * either configured bound is unknown — the caller then shows no window hint rather than judging
 * against a hardcoded default, since the config can change at any time.
 */
export function vppWindowState({
  vppCents,
  vppFloorCents,
  vppCeilingCents,
}: {
  vppCents: number | null;
  vppFloorCents: number | null;
  vppCeilingCents: number | null;
}): VppWindowState | null {
  if (vppCents == null || vppFloorCents == null || vppCeilingCents == null) return null;
  if (vppCents < vppFloorCents) return 'below_floor';
  if (vppCents > vppCeilingCents) return 'above_ceiling';
  return 'within';
}

/** Formats a margin fraction as a percentage string (1dp), or "n/a" when null. */
export function formatMarginPercent(margin: number | null): string {
  return margin === null ? 'n/a' : `${(margin * 100).toFixed(1)}%`;
}

/** Formats a VPP (already in cents per point, 2dp) with a "/ point" suffix, or "n/a" when null. */
export function formatVppCents(vppCents: number | null): string {
  return vppCents === null ? 'n/a' : `${vppCents.toFixed(2)}¢ / point`;
}

/**
 * Formula-side points/copay inputs for the reward margin / VPP comparison shown in the override
 * dialog: the reward's last-computed base points, and the AUD price row's server-computed base
 * copay (`societe_reward_computed_copay_base`) — as opposed to the override/effective inputs,
 * which reflect the dialog's live form state (override input taking precedence over the live
 * value).
 *
 * `societe_reward_computed_copay_base` is a newly added, optional API field. When it is absent
 * entirely (`undefined` — no matching AUD price row, or the backend hasn't started sending it
 * yet), `copayAvailable` is `false` so callers render "n/a" for the formula value instead of
 * silently treating the missing copay as a zero. An explicit `null` (the field is present but
 * this currency has no base copay) IS a confirmed zero and is distinguished from "unknown".
 */
export function formulaRewardInputs({
  computedPointsBase,
  audPrice,
}: {
  computedPointsBase: number;
  audPrice: { societe_reward_computed_copay_base?: number | null } | undefined;
}): { points: number; copayAud: number | null; copayAvailable: boolean } {
  const copayAvailable = audPrice?.societe_reward_computed_copay_base !== undefined;
  return {
    points: computedPointsBase,
    copayAud: audPrice?.societe_reward_computed_copay_base ?? null,
    copayAvailable,
  };
}
